import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  ArrowLeft, 
  ChevronLeft,
  ChevronRight,
  Heart, 
  Swords, 
  Shield, 
  Zap, 
  Star
} from 'lucide-react';
// import toast from 'react-hot-toast';
// Image utilities now handled by PokemonImageGallery component
import TypeEffectivenessChart from '../components/TypeEffectivenessChart';
import PokemonCounters from '../components/PokemonCounters';
import PokemonMovesSection from '../components/PokemonMovesSection';
import PokemonImageGallery from '../components/PokemonImageGallery';
import { PageContainer, SectionHeader, TypeBadge, DataPanel, MetricDisplay } from '../components/shared';
import { useLanguage } from '../contexts/LanguageContext';
import { getPokemonImage } from '../utils/pokemonImageResolver';
import { getApiBaseUrl } from '../utils/apiUrl';

const PokemonDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, language, gamePokemonName } = useLanguage();
  const [pokemon, setPokemon] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [previousPokemon, setPreviousPokemon] = useState(null);
  const [nextPokemon, setNextPokemon] = useState(null);

  useEffect(() => {
    // Scroll to top when component mounts or ID changes
    window.scrollTo({ top: 0, behavior: 'smooth' });
    
    // Reset state when ID changes
    setPokemon(null);
    setError(null);
    setLoading(true);
    setPreviousPokemon(null);
    setNextPokemon(null);
    fetchPokemonDetail();
  }, [id]); // eslint-disable-line react-hooks/exhaustive-deps

  const fetchPokemonDetail = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await fetch(`${getApiBaseUrl()}/api/pokemon/${id}`);
      const data = await response.json();

      if (data.success) {
        setPokemon(data.data);
        
        // Fetch previous and next Pokemon
        await fetchAdjacentPokemon(data.data.Pokemon_ID || data.data.id);
      } else {
        setError(data.error || t('detail.notFound'));
      }
    } catch (error) {
      console.error('Error fetching Pokemon detail:', error);
      setError(t('detail.loadError'));
    } finally {
      setLoading(false);
    }
  };

  const fetchAdjacentPokemon = async (currentId) => {
    try {
      // Extract numeric ID for adjacent lookup
      const numericId = parseInt(String(currentId).split('-')[0].replace(/^0+/, '')) || 0;
      
      if (numericId === 0) {
        setPreviousPokemon(null);
        setNextPokemon(null);
        return;
      }
      
      // Helper function to try multiple ID formats
      const tryFetchPokemon = async (idNum) => {
        const formats = [
          String(idNum), // Try without padding first (e.g., "2")
          String(idNum).padStart(4, '0'), // Try with padding (e.g., "0002")
          String(idNum).padStart(3, '0') // Try 3-digit padding (e.g., "002")
        ];
        
        for (const formatId of formats) {
          try {
            const response = await fetch(`${getApiBaseUrl()}/api/pokemon/${formatId}`);
            const data = await response.json();
            if (data.success && data.data) {
              return data.data;
            }
          } catch (err) {
            // Try next format
            continue;
          }
        }
        return null;
      };
      
      // Fetch previous Pokemon (ID - 1)
      if (numericId > 1) {
        const prevData = await tryFetchPokemon(numericId - 1);
        setPreviousPokemon(prevData);
      } else {
        setPreviousPokemon(null);
      }
      
      // Fetch next Pokemon (ID + 1)
      const nextData = await tryFetchPokemon(numericId + 1);
      setNextPokemon(nextData);
      
    } catch (error) {
      console.error('Error fetching adjacent Pokemon:', error);
      setPreviousPokemon(null);
      setNextPokemon(null);
    }
  };

  const formatTypes = (typesString) => {
    if (!typesString) return [];
    return typesString.split(',').map(type => type.trim());
  };


  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-8 h-8 border-4 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">{t('common.loading')}</p>
        </div>
      </div>
    );
  }

  if (error || !pokemon) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-purple-50 flex items-center justify-center">
        <div className="text-center">
          <div className="text-red-500 text-lg mb-4">{t('common.error')}: {error}</div>
          <button 
            onClick={() => navigate('/')}
            className="btn-primary"
          >
            {t('detail.goHome')}
          </button>
        </div>
      </div>
    );
  }

  const types = formatTypes(pokemon.types);

  return (
    <div className="min-h-screen bg-gray-50">
      <PageContainer maxWidth="7xl" padding={true} className="py-8">
        {/* Pokemon Navigation - Previous/Next */}
        {pokemon && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            className="flex items-center justify-between gap-4 mb-6 px-2"
          >
            {/* Previous Pokemon */}
            {previousPokemon ? (
              <button
                onClick={() => navigate(`/pokemon/${previousPokemon.Pokemon_ID || previousPokemon.id}`)}
                className="flex items-center gap-3 px-4 py-3 bg-white rounded-2xl shadow-md hover:shadow-lg transition-all duration-200 hover:bg-gray-50 flex-1 max-w-xs group"
              >
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl flex items-center justify-center border-2 border-gray-300 overflow-hidden">
                  <img
                    src={getPokemonImage(previousPokemon)}
                    alt={previousPokemon.Pokemon_Name_TH || previousPokemon.Pokemon_Name_EN}
                    className="w-full h-full object-contain p-1"
                    onError={(e) => {
                      e.target.src = '/images/pokemon-spirit/000.png';
                    }}
                  />
                </div>
                <div className="flex-1 min-w-0 text-left">
                  <div className="flex items-center gap-2 mb-1">
                    <ChevronLeft className="w-4 h-4 text-gray-400 group-hover:text-blue-500 transition-colors" />
                    <span className="text-xs text-gray-500 font-medium">{t('detail.previous')}</span>
                  </div>
                  <div className="font-bold text-gray-900 truncate text-base">
                    {gamePokemonName(previousPokemon.Pokemon_ID || previousPokemon.id) || (language === 'th' ? previousPokemon.Pokemon_Name_TH : previousPokemon.Pokemon_Name_EN) || previousPokemon.Pokemon_Name_EN}
                  </div>
                  <div className="text-xs text-gray-500 font-mono">
                    #{previousPokemon.Pokemon_ID || previousPokemon.id}
                  </div>
                </div>
              </button>
            ) : (
              <div className="flex-1 max-w-xs"></div>
            )}

            {/* Next Pokemon */}
            {nextPokemon ? (
              <button
                onClick={() => navigate(`/pokemon/${nextPokemon.Pokemon_ID || nextPokemon.id}`)}
                className="flex items-center gap-3 px-4 py-3 bg-white rounded-2xl shadow-md hover:shadow-lg transition-all duration-200 hover:bg-gray-50 flex-1 max-w-xs group ml-auto"
              >
                <div className="flex-1 min-w-0 text-right">
                  <div className="flex items-center justify-end gap-2 mb-1">
                    <span className="text-xs text-gray-500 font-medium">{t('detail.next')}</span>
                    <ChevronRight className="w-4 h-4 text-gray-400 group-hover:text-blue-500 transition-colors" />
                  </div>
                  <div className="font-bold text-gray-900 truncate text-base">
                    {gamePokemonName(nextPokemon.Pokemon_ID || nextPokemon.id) || (language === 'th' ? nextPokemon.Pokemon_Name_TH : nextPokemon.Pokemon_Name_EN) || nextPokemon.Pokemon_Name_EN}
                  </div>
                  <div className="text-xs text-gray-500 font-mono">
                    #{nextPokemon.Pokemon_ID || nextPokemon.id}
                  </div>
                </div>
                <div className="flex-shrink-0 w-12 h-12 bg-gradient-to-br from-gray-100 to-gray-200 rounded-xl flex items-center justify-center border-2 border-gray-300 overflow-hidden">
                  <img
                    src={getPokemonImage(nextPokemon)}
                    alt={nextPokemon.Pokemon_Name_TH || nextPokemon.Pokemon_Name_EN}
                    className="w-full h-full object-contain p-1"
                    onError={(e) => {
                      e.target.src = '/images/pokemon-spirit/000.png';
                    }}
                  />
                </div>
              </button>
            ) : (
              <div className="flex-1 max-w-xs"></div>
            )}
          </motion.div>
        )}

        {/* Section Header - Back button removed (using navigation bar instead) */}
        <SectionHeader
          title={gamePokemonName(pokemon?.Pokemon_ID || id) || (language === 'th' ? pokemon?.Pokemon_Name_TH : pokemon?.Pokemon_Name_EN) || pokemon?.Pokemon_Name_EN || t('detail.title')}
          subtitle={language === 'th' ? pokemon?.Pokemon_Name_EN : pokemon?.Pokemon_Name_TH}
        />

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          {/* Left Column - Pokemon Info and Stats */}
          <div className="space-y-6">
            {/* Pokemon Image and Basic Info */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
              className="bg-white rounded-3xl p-8 shadow-pokemon"
            >
              {/* Pokemon Image Gallery - Modern with Shiny Toggle */}
              {pokemon && (
                <PokemonImageGallery
                  key={pokemon.Pokemon_ID}
                  pokemonId={pokemon.Pokemon_ID}
                  pokemonName={pokemon.Pokemon_Name_EN}
                  pokemonNameTh={pokemon.Pokemon_Name_TH}
                  hasShiny={pokemon.has_shiny === 1}
                  type1={types[0] || 'Normal'}
                  className="mb-6"
                />
              )}

              {/* Pokemon Names */}
              <div className="text-center mb-6">
                <h1 className="text-3xl font-bold text-gray-800 mb-2">
                  {gamePokemonName(pokemon.Pokemon_ID || id) || (language === 'th' ? pokemon.Pokemon_Name_TH : pokemon.Pokemon_Name_EN) || pokemon.Pokemon_Name_EN}
                </h1>
                <h2 className="text-2xl font-semibold text-gray-600 mb-2">
                  {language === 'th' ? pokemon.Pokemon_Name_EN : pokemon.Pokemon_Name_TH}
                </h2>
                <p className="text-lg text-gray-500 font-mono">
                  #{pokemon.Pokemon_ID}
                </p>
                
                {/* Special Form Badges */}
                <div className="flex items-center justify-center gap-3 mt-4 flex-wrap">
                  {pokemon.has_shiny === 1 && (
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-yellow-400 to-orange-500 rounded-full shadow-md" title={t('badge.shinyTitle')}>
                      <img src="/images/shiny_icon.webp" alt="Shiny" className="w-5 h-5" />
                      <span className="text-white text-sm font-semibold">{t('badge.shiny')}</span>
                    </div>
                  )}
                  
                  {pokemon.can_gigantamax === 1 && (
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-pink-500 to-purple-600 rounded-full shadow-md" title={t('badge.gigantamaxTitle')}>
                      <img src="/images/gigantamax_icon.png" alt={t('badge.gigantamax')} className="w-5 h-5 object-contain" />
                      <span className="text-white text-sm font-semibold">{t('badge.gigantamax')}</span>
                    </div>
                  )}
                  
                  {pokemon.has_mega === 1 && (
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-blue-500 to-indigo-600 rounded-full shadow-md" title={t('badge.megaTitle')}>
                      <span className="w-5 h-5 bg-white rounded-full text-blue-600 text-sm font-bold flex items-center justify-center">M</span>
                      <span className="text-white text-sm font-semibold">{t('badge.mega')}</span>
                    </div>
                  )}
                  
                  {pokemon.can_dynamax === 1 && (
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-red-500 to-pink-500 rounded-full shadow-md" title={t('badge.dynamaxTitle')}>
                      <img src="/images/dynanmax_icon.webp" alt={t('badge.dynamax')} className="w-5 h-5" />
                      <span className="text-white text-sm font-semibold">{t('badge.dynamax')}</span>
                    </div>
                  )}
                  
                  {pokemon.legendary === 1 && (
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-yellow-500 to-amber-600 rounded-full shadow-md" title={t('badge.legendary')}>
                      <Star className="w-5 h-5 text-white" />
                      <span className="text-white text-sm font-semibold">{t('badge.legendary')}</span>
                    </div>
                  )}
                  
                  {pokemon.mythical === 1 && (
                    <div className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-purple-500 to-pink-500 rounded-full shadow-md" title={t('badge.mythical')}>
                      <Star className="w-5 h-5 text-white" />
                      <span className="text-white text-sm font-semibold">{t('badge.mythical')}</span>
                    </div>
                  )}
                </div>
              </div>

              {/* Types */}
              <div className="flex flex-wrap justify-center gap-3 mb-6">
                {types.map((type, idx) => (
                  <TypeBadge key={idx} type={type} size="default" />
                ))}
              </div>
            </motion.div>

            {/* Pokemon Stats */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="space-y-6"
            >
            {/* Combat Power */}
            <DataPanel
              title={t('detail.combatPower')}
              variant="stats"
            >
              <div className="text-center">
                <MetricDisplay
                  label={t('detail.maxCp')}
                  value={pokemon.Max_CP_Lv50 ? pokemon.Max_CP_Lv50.toLocaleString() : 'N/A'}
                  variant="large"
                  align="center"
                />
              </div>
            </DataPanel>

            {/* Base Stats */}
            <div className="bg-white rounded-3xl p-6 shadow-pokemon">
              <h3 className="text-xl font-semibold text-gray-800 mb-6">{t('detail.baseStats')}</h3>
              
              <div className="space-y-4">
                {/* HP */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <Heart className="w-5 h-5 text-red-500" />
                      <span className="font-medium text-gray-700">{t('stats.hp')}</span>
                    </div>
                    <span className="font-semibold text-gray-800">
                      {pokemon.HP || 'N/A'}
                    </span>
                  </div>
                  <div className="stat-bar">
                    <div 
                      className="stat-fill bg-red-500"
                      style={{ width: `${pokemon.HP ? (pokemon.HP / 300) * 100 : 0}%` }}
                    ></div>
                  </div>
                </div>

                {/* Attack */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <Swords className="w-5 h-5 text-orange-500" />
                      <span className="font-medium text-gray-700">{t('stats.attack')}</span>
                    </div>
                    <span className="font-semibold text-gray-800">
                      {pokemon.Attack || 'N/A'}
                    </span>
                  </div>
                  <div className="stat-bar">
                    <div 
                      className="stat-fill bg-orange-500"
                      style={{ width: `${pokemon.Attack ? (pokemon.Attack / 300) * 100 : 0}%` }}
                    ></div>
                  </div>
                </div>

                {/* Defense */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <div className="flex items-center space-x-2">
                      <Shield className="w-5 h-5 text-blue-500" />
                      <span className="font-medium text-gray-700">{t('stats.defense')}</span>
                    </div>
                    <span className="font-semibold text-gray-800">
                      {pokemon.Defense || 'N/A'}
                    </span>
                  </div>
                  <div className="stat-bar">
                    <div 
                      className="stat-fill bg-blue-500"
                      style={{ width: `${pokemon.Defense ? (pokemon.Defense / 300) * 100 : 0}%` }}
                    ></div>
                  </div>
                </div>
              </div>
            </div>

            {/* Gigantamax Move - Only show for Gigantamax Pokemon with actual moves */}
            {pokemon.Gigantamax_Move && 
             pokemon.Gigantamax_Move !== 'None' && 
             pokemon.Gigantamax_Move !== '' &&
             (pokemon.Pokemon_ID?.includes('Gigantamax') || pokemon.form_type === 'gmax') && (
              <div className="bg-white rounded-3xl p-6 shadow-pokemon">
                <h3 className="text-xl font-semibold text-gray-800 mb-4">{t('detail.gmaxMove')}</h3>
                
                <div className="text-center">
                  <div className="flex items-center justify-center space-x-2 mb-2">
                    <Star className="w-6 h-6 text-purple-500" />
                    <span className="text-lg font-medium text-gray-700">{t('detail.specialMove')}</span>
                  </div>
                  <div className="text-xl font-semibold text-purple-600">
                    {pokemon.Gigantamax_Move}
                  </div>
                </div>
              </div>
            )}

            {/* Type Effectiveness Chart */}
            <TypeEffectivenessChart 
              pokemonId={pokemon.Pokemon_ID} 
              pokemonName={pokemon.Pokemon_Name_EN}
            />
            </motion.div>
          </div>

          {/* Right Column - Pokemon Counters & Moves */}
          <div className="space-y-6">
            {/* Pokemon Counters from CSV data */}
            <PokemonCounters 
              pokemonId={pokemon.Pokemon_ID} 
              pokemonName={pokemon.Pokemon_Name_EN}
            />

            {/* Pokemon Moves Section - All Moves & Best Movesets */}
            <PokemonMovesSection 
              pokemonId={pokemon.Pokemon_ID} 
              pokemonName={pokemon.Pokemon_Name_EN}
            />
          </div>
        </div>
      </PageContainer>
    </div>
  );
};

export default PokemonDetail;
